import { useCallback, useEffect, useState } from 'react';
import { isTypingTarget } from '../utils/review-keys';

export type FlipView = 'current' | 'new';

const NO_PEEK_SELECTOR = 'button, a, [role="button"], [data-review-canvas]';

export function useFlipView(reviewKey: string | null): { shown: FlipView; choose: (view: FlipView) => void } {
  const [state, setState] = useState<{ key: string | null; chosen: FlipView; peeking: boolean }>({
    key: reviewKey,
    chosen: 'new',
    peeking: false,
  });
  if (state.key !== reviewKey) {
    setState({ key: reviewKey, chosen: 'new', peeking: false });
  }

  useEffect(() => {
    if (reviewKey === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== ' ') return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (event.defaultPrevented || isTypingTarget(event.target)) return;
      if (event.target instanceof Element && event.target.closest(NO_PEEK_SELECTOR)) return;
      event.preventDefault();
      setState((s) => (s.peeking ? s : { ...s, peeking: true }));
    };
    const endPeek = () => setState((s) => (s.peeking ? { ...s, peeking: false } : s));
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === ' ') endPeek();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', endPeek);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', endPeek);
    };
  }, [reviewKey]);

  const choose = useCallback((view: FlipView) => setState((s) => ({ ...s, chosen: view })), []);

  return { shown: state.peeking ? 'current' : state.chosen, choose };
}
